import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Link,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ContentCut as ScissorsIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

const UserTypeSelection = ({ userType, setUserType, onNext, onSwitchToSignIn }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const handleUserTypeSelection = (type) => {
    setUserType(type);
    // On mobile, automatically proceed to next step after selection
    if (isMobile) {
      setTimeout(() => {
        onNext();
      }, 300); // Small delay to show the selection animation
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            backgroundColor: '#2a2a2a',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >

          <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 6 } }}>
            {/* Header */}
            <Box textAlign="center" mb={{ xs: 2, md: 6 }}>
              <Box display="flex" justifyContent="center" mb={{ xs: 1, md: 3 }}>
                <Box
                  sx={{
                    width: { xs: 50, md: 80 },
                    height: { xs: 50, md: 80 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 1, md: 2 },
                    p: { xs: 1, md: 1.5 }
                  }}
                >
                  <img
                    src="/Logos/logoFocus.png"
                    alt="BookingBarber"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
              
              <Typography
                variant={isMobile ? "h5" : "h2"}
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: { xs: 1, md: 2 }
                }}
              >
                {t('auth.joinBookingBarber')}
              </Typography>
              
              {!isMobile && (
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(255,255,255,0.8)'
                  }}
                >
                  <strong style={{ color: '#f5f5dc' }}>{t('auth.organizeCalendar')}</strong> {t('auth.developClientele')}
                </Typography>
              )}
              
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={{ xs: 1.5, md: 4 }}
                mb={{ xs: 1, md: 4 }}
                flexWrap="wrap"
              >
                {[t('auth.noCommitment'), t('auth.noCommission'), t('auth.free')].map((text, index) => (
                  <Box
                    key={text}
                    display="flex"
                    alignItems="center"
                    gap={{ xs: 0.3, md: 1 }}
                    sx={{
                      animation: `fadeInUp 0.8s ease-out ${index * 0.2}s both`,
                      '@keyframes fadeInUp': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(20px)'
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0)'
                        }
                      }
                    }}
                  >
                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: { xs: 14, md: 20 } }} />
                    <Typography
                      variant={{ xs: 'caption', md: 'body1' }}
                      sx={{
                        fontWeight: 500,
                        background: 'linear-gradient(45deg, white 0%, #f5f5dc 50%, white 100%)',
                        backgroundSize: '200% 100%',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'textShine 3s ease-in-out infinite',
                        '@keyframes textShine': {
                          '0%': { backgroundPosition: '0% 50%' },
                          '50%': { backgroundPosition: '100% 50%' },
                          '100%': { backgroundPosition: '0% 50%' }
                        }
                      }}
                    >
                      {text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* User Type Selection */}
            <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mb: { xs: 3, md: 6 } }}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    border: userType === 'professional' ? '3px solid #f5f5dc' : '2px solid rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    background: userType === 'professional'
                      ? 'linear-gradient(135deg, rgba(245,245,220,0.1) 0%, rgba(232,232,200,0.1) 100%)'
                      : 'rgba(255,255,255,0.05)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(245, 245, 220, 0.2)',
                      border: '3px solid #f5f5dc',
                      background: 'rgba(245,245,220,0.1)'
                    }
                  }}
                  onClick={() => handleUserTypeSelection('professional')}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f5f5dc, #e8e8c8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <ScissorsIcon sx={{ fontSize: 40, color: '#212121' }} />
                    </Box>
                    
                    <Typography variant={{ xs: 'h6', md: 'h5' }} gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                      {t('auth.establishmentManager')}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
                      {t('auth.beautyServices')}
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: userType === 'professional' ? '#f5f5dc' : 'rgba(255,255,255,0.6)',
                        fontWeight: 600
                      }}
                    >
                      <Typography variant="body2">{t('auth.choose')}</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    height: '100%',
                    border: userType === 'client' ? '3px solid #f5f5dc' : '2px solid rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    background: userType === 'client'
                      ? 'linear-gradient(135deg, rgba(245,245,220,0.1) 0%, rgba(232,232,200,0.1) 100%)'
                      : 'rgba(255,255,255,0.05)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(245, 245, 220, 0.2)',
                      border: '3px solid #f5f5dc',
                      background: 'rgba(245,245,220,0.1)'
                    }
                  }}
                  onClick={() => handleUserTypeSelection('client')}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f5f5dc, #e8e8c8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, color: '#212121' }} />
                    </Box>
                    
                    <Typography variant={{ xs: 'h6', md: 'h5' }} gutterBottom sx={{ fontWeight: 700, mb: 2, color: 'white' }}>
                      {t('auth.notProfessional')}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
                      {t('auth.bookAppointment')}
                    </Typography>
                    
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: userType === 'client' ? '#f5f5dc' : 'rgba(255,255,255,0.6)',
                        fontWeight: 600
                      }}
                    >
                      <Typography variant="body2">{t('auth.choose')}</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 20 }} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Continue Button */}
            <Box display="flex" justifyContent="center" mb={4}>
              <Button
                variant="contained"
                size="large"
                disabled={!userType}
                onClick={onNext}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 6,
                  py: 2,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                  color: '#212121',
                  boxShadow: '0 8px 25px rgba(245, 245, 220, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #e8e8c8 0%, #dcdcb8 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 35px rgba(245, 245, 220, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)'
                  }
                }}
              >
                {t('auth.continue')}
              </Button>
            </Box>

            {/* Footer Links */}
            <Box textAlign="center">
              <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
                {t('auth.alreadyHaveAccountQuestion')}{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onSwitchToSignIn}
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    color: '#f5f5dc',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {t('auth.signInLink')}
                </Link>
              </Typography>
              
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Pas un professionnel ? Prenez rendez-vous sur{' '}
                <Link
                  href="/"
                  sx={{
                    color: '#f5f5dc',
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  BookingBarber.com
                </Link>
              </Typography>
            </Box>
          </Box>
          
          {/* Mobile scroll indicator */}
          {isMobile && (
            <Box
              sx={{
                position: 'fixed',
                bottom: 20,
                right: 20,
                zIndex: 1000,
                display: { xs: 'block', md: 'none' }
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 20px rgba(245, 245, 220, 0.3)',
                  cursor: 'pointer',
                  animation: 'bounce 2s infinite',
                  '@keyframes bounce': {
                    '0%, 20%, 50%, 80%, 100%': {
                      transform: 'translateY(0)'
                    },
                    '40%': {
                      transform: 'translateY(-10px)'
                    },
                    '60%': {
                      transform: 'translateY(-5px)'
                    }
                  }
                }}
                onClick={() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: 'smooth'
                  });
                }}
              >
                <KeyboardArrowDownIcon sx={{ color: '#212121', fontSize: 24 }} />
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default UserTypeSelection;